import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { dateInputToUnix } from '@/shared/lib/dateInput';
import { formatUnixDateTime } from '@/shared/lib/datetime';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import { DateTimeInput } from '@/shared/ui/DateTimeInput';
import { FilterField } from '@/shared/ui/FilterField';
import { Pagination } from '@/shared/ui/Pagination';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { fetchCancelledBidsMessages, fetchCancelledBidsStats, type CancelledBidsQuery } from './api';

interface StatRow extends Record<string, unknown> {
  count?: number | string;
  percent?: number | string;
}

interface BidStatusStat extends StatRow {
  bid_status_id: number;
}

interface ReasonStat extends StatRow {
  type_id: number;
}

interface MessageRow extends Record<string, unknown> {
  bid_id: number | string;
  service_id?: number | string | null;
  customer_id?: number | string;
  description?: string;
  create_time?: number;
}

const BID_STATUS: Record<number, string> = {
  0: 'Новая',
  1: 'Визит',
  2: 'В работе',
};

const REASON_TYPE: Record<number, string> = {
  1: 'Другое',
  2: 'Нет ответов от СТО',
  3: 'Не актуально',
  4: 'СТО не связалось со мной',
  5: 'Не устроила стоимость работ',
  6: 'СТО не принимает онлайн оплату',
  7: 'СТО очень далеко',
};

interface Filters {
  dateFrom: string;
  dateTo: string;
}

const emptyFilters: Filters = { dateFrom: '', dateTo: '' };

const hasSearchData = (f: Filters) => Boolean(f.dateFrom || f.dateTo);

const toQuery = (f: Filters): CancelledBidsQuery => ({
  create_time_from: dateInputToUnix(f.dateFrom),
  create_time_to: dateInputToUnix(f.dateTo),
});

/** Legacy paginated by `pageSize` / `totalCount` for this endpoint; fall back to `pagesCount`. */
const getPagesCount = (data: unknown): number => {
  const pagination = (data as { pagination?: { pageSize?: number; totalCount?: number } } | undefined)?.pagination;
  const pageSize = Number(pagination?.pageSize);
  const totalCount = Number(pagination?.totalCount);
  return pageSize > 0 && totalCount > 0 ? Math.ceil(totalCount / pageSize) : getTotalPages(data);
};

const StatisticTable = ({
  labelHeader,
  rows,
}: {
  labelHeader: string;
  rows: Array<{ key: number; label: string; count?: number | string; percent?: number | string }>;
}) => (
  <table className="table table-bordered">
    <thead>
      <tr>
        <th>{labelHeader}</th>
        <th>Количество</th>
        <th>Процент %</th>
      </tr>
    </thead>
    <tbody>
      {rows.map(({ key, label, count, percent }) => (
        <tr key={key}>
          <td>{label}</td>
          <td>{count}</td>
          <td>{percent}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export const CancelledBidsPage = () => {
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState<Filters>(emptyFilters);
  const [applied, setApplied] = useState<Filters | null>(null);

  const statsQuery = useQuery({
    queryKey: ['cancelled-bids-stats', applied],
    queryFn: () => fetchCancelledBidsStats(toQuery(applied!)),
    enabled: applied !== null,
  });

  const messagesQuery = useQuery({
    queryKey: ['cancelled-bids-messages', applied, page],
    queryFn: () => fetchCancelledBidsMessages(toQuery(applied!), page),
    enabled: applied !== null,
  });

  const statuses = getListRows<BidStatusStat>(statsQuery.data, ['bid_status_statistic']);
  const reasons = getListRows<ReasonStat>(statsQuery.data, ['reason_statistic']);
  const messages = getListRows<MessageRow>(messagesQuery.data, ['items']);
  const totalCancelled = statuses.reduce((sum, { count }) => sum + Number(count), 0);

  const setDraftField = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    if (!hasSearchData(draft)) return;
    setPage(1);
    setApplied({ ...draft });
  };

  const isFetching = statsQuery.isFetching || messagesQuery.isFetching;

  return (
    <>
      <section className="content-header">
        <h1>Отменённые заявки</h1>
      </section>
      <section className="content">
        <div className="box">
          <div className="box-header with-border">
            <form onSubmit={onSearch}>
              <FilterField label="Начиная с даты">
                <DateTimeInput
                  type="date"
                  value={draft.dateFrom}
                  max={draft.dateTo || undefined}
                  onChange={(v) => setDraftField('dateFrom', v)}
                />
              </FilterField>
              <FilterField label="Заканчивая датой">
                <DateTimeInput
                  type="date"
                  value={draft.dateTo}
                  min={draft.dateFrom || undefined}
                  onChange={(v) => setDraftField('dateTo', v)}
                />
              </FilterField>
              <button type="submit" className="btn btn-primary" disabled={!hasSearchData(draft) || isFetching}>
                Найти
              </button>
            </form>
          </div>

          <div className="box-body">
            <QueryStatus
              isLoading={statsQuery.isLoading || messagesQuery.isLoading}
              error={statsQuery.error ?? messagesQuery.error}
              errorText="Ошибка загрузки отменённых заявок"
            />
            <p>
              <strong>Всего отменённых заявок: {totalCancelled}</strong>
            </p>

            <div className="row">
              <div className="col-md-6">
                <p>
                  <strong>Статистика по статусу</strong>
                </p>
                <StatisticTable
                  labelHeader="Статус"
                  rows={statuses.map((s) => ({
                    key: s.bid_status_id,
                    label: BID_STATUS[s.bid_status_id] ?? '',
                    count: s.count,
                    percent: s.percent,
                  }))}
                />
              </div>
              <div className="col-md-6">
                <p>
                  <strong>Статистика по причинам</strong>
                </p>
                <StatisticTable
                  labelHeader="Причина"
                  rows={reasons.map((r) => ({
                    key: r.type_id,
                    label: REASON_TYPE[r.type_id] ?? '',
                    count: r.count,
                    percent: r.percent,
                  }))}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-8">
                <p>
                  <strong>Заявки с причиной отказа &quot;Другое&quot;</strong>
                </p>
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Дата и время</th>
                      <th>ID заявки</th>
                      <th>ID СТО</th>
                      <th>ID клиента</th>
                      <th>Комментарий</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((m) => (
                      <tr key={m.bid_id}>
                        <td>{formatUnixDateTime(m.create_time)}</td>
                        <td>
                          <Link to={`/bids/bid/${m.bid_id}`}>{m.bid_id}</Link>
                        </td>
                        <td>{m.service_id ? <Link to={`/users/user/${m.service_id}`}>{m.service_id}</Link> : '-'}</td>
                        <td>
                          <Link to={`/users/user/${m.customer_id}`}>{m.customer_id}</Link>
                        </td>
                        <td>{m.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <Pagination page={page} totalPages={getPagesCount(messagesQuery.data)} onChange={setPage} />
        </div>
      </section>
    </>
  );
};
